/* ELIXIR ACADEMY portal renderer.
   One module per page. Two modes: quick reference and full course.
   Language is chosen inside the topic, so a learner never leaves the page to
   switch between Korean and English. Progress is per module, per mode and per
   language, and is kept in this browser only. */
(function () {
  'use strict';

  var LABELS = {
    ko: {
      previous: '이전', next: '다음', complete: '이 단계 완료', completed: '완료됨',
      progress: '진행률', outline: '과정 구성', jump: '레슨 바로가기',
      concept: '핵심 개념', practice: '실무 적용', caution: '주의 사항',
      products: 'Complete Elixir 제품 역할', quiz: '지식 확인',
      correct: '정답입니다.', incorrect: '다시 확인해 보세요.',
      lesson: '레슨', step: '단계', quick: '현장 모드', full: '정규 학습', home: 'ACADEMY',
      backToLibrary: '모듈 라이브러리로',
      watch: '강의 영상 보기', newTab: '새 창에서 열립니다',
      videoLocked: '강의 영상 · 승인 회원 전용',
      videoPending: '승인이 완료되면 이 자리에 영상 버튼이 나타납니다.',
      login: '로그인', loginTail: '하시면 승인된 회원은 영상을 보실 수 있습니다.'
    },
    en: {
      previous: 'Previous', next: 'Next', complete: 'Complete', completed: 'Completed',
      progress: 'Progress', outline: 'Course outline', jump: 'Jump to a lesson',
      concept: 'Core concept', practice: 'Practical application', caution: 'Caution',
      products: 'Complete Elixir product role', quiz: 'Knowledge check',
      correct: 'Correct.', incorrect: 'Review and try again.',
      lesson: 'Lesson', step: 'Step', quick: 'Quick reference', full: 'Full course', home: 'ACADEMY',
      backToLibrary: 'Back to library',
      watch: 'Watch the lecture', newTab: 'Opens in a new tab',
      videoLocked: 'Lecture video · Approved members only',
      videoPending: 'The video button appears here once your account is approved.',
      login: 'Sign in', loginTail: ' and approved members can watch the video.'
    }
  };

  /* The language buttons live in the masthead. The module root also carries a
     data-lang attribute, because that is how the template states which
     language to open in, so a bare [data-lang] search from a click target
     bubbles all the way up and finds the root. That made every click on
     anything that was not a step — a state selector, a quiz option — look like
     a request to switch to the template's default language, and a reader who
     had chosen Korean was thrown back to English. Every lookup of the language
     buttons is scoped to the switch itself. */
  var LANG_BTN = '.lang-sw [data-lang]';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  function pad(n) { return String(n).padStart(2, '0'); }

  function store(key, fallback) {
    try {
      var v = localStorage.getItem(key);
      return v == null ? fallback : v;
    } catch (e) { return fallback; }
  }
  function put(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  /* Build an element and fill it from an HTML string, so whatever the string
     contains is parsed and repaired inside that element and cannot affect its
     siblings. */
  function el(cls, html) {
    var d = document.createElement('div');
    if (cls) d.className = cls;
    d.innerHTML = html;
    return d;
  }

  window.ceAcademy = function (root) {
    var dataEl = root.querySelector('[data-ce-academy-data]') ||
      document.querySelector('[data-ce-academy-data]');
    if (!dataEl) return;

    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var mode = root.dataset.mode === 'full' ? 'full' : 'quick';
    if (!data[mode]) mode = data.quick ? 'quick' : 'full';
    var payloadSet = data[mode];
    if (!payloadSet) return;
    root.dataset.mode = mode;

    var langs = Object.keys(payloadSet);
    var storedLang = store('ce-ac-lang', null);
    var lang = langs.indexOf(storedLang) > -1 ? storedLang
      : (langs.indexOf(root.dataset.lang) > -1 ? root.dataset.lang : langs[0]);

    var keyBase, P, L, items, active, done;

    function readProgress() {
      keyBase = 'ce-ac-' + data.module + '-' + mode + '-' + lang;
      active = parseInt(store(keyBase + '-active', '0'), 10) || 0;
      try { done = new Set(JSON.parse(store(keyBase + '-complete', '[]'))); }
      catch (e) { done = new Set(); }
      if (active < 0 || active >= items.length) active = 0;
    }
    function saveProgress() {
      put(keyBase + '-active', String(active));
      put(keyBase + '-complete', JSON.stringify(Array.from(done)));
    }

    function load() {
      if (!payloadSet[lang]) lang = langs[0];
      P = payloadSet[lang];
      L = LABELS[lang] || LABELS.en;
      items = P.items || [];
      readProgress();
    }

    /* ---------- lecture recording ---------- */
    /* A module may attach a recording to a lesson in data.video, keyed by mode,
       language and lesson index. The access check is done in Liquid: a reader
       with access receives the URL, everyone else an empty string, which is
       drawn as the members only panel. A lesson with no key gets no row. */
    function videoHtml(i) {
      var v = data.video && data.video[mode] && data.video[mode][lang];
      if (!v || !Object.prototype.hasOwnProperty.call(v, String(i))) return '';
      var url = v[String(i)];
      if (url) {
        return '<a class="vbtn" href="' + esc(url) + '" target="_blank" rel="noopener"><i aria-hidden="true"></i>' +
          esc(L.watch) + '</a><span class="vnote">' + esc(L.newTab) + '</span>';
      }
      return '<span class="vlock"><i aria-hidden="true"></i>' + esc(L.videoLocked) + '</span><span class="vnote">' +
        (data.videoSignedIn ? esc(L.videoPending)
          : '<a href="' + esc(data.videoLogin || '/account/login') + '">' + esc(L.login) + '</a>' + esc(L.loginTail)) +
        '</span>';
    }

    /* ---------- composing a lesson body ---------- */
    function list(arr, cls) {
      return '<div class="' + cls + '">' + arr.map(function (v, i) {
        return '<div class="item"><b>' + pad(i + 1) + '</b><div>' +
          (v.t ? '<strong>' + esc(v.t) + '</strong>' : '') +
          '<p>' + (v.d || v) + '</p></div></div>';
      }).join('') + '</div>';
    }

    function compose(item) {
      if (item.body) return item.body;
      var h = '';
      if (item.concept && item.concept.length) {
        h += '<section class="sec"><p class="section-label">' + esc(L.concept) + '</p>' +
          '<div class="concept">' + item.concept.map(function (p) { return '<p>' + p + '</p>'; }).join('') + '</div></section>';
      }
      if (item.practice && item.practice.length) {
        h += '<section class="sec"><p class="section-label">' + esc(L.practice) + '</p>' +
          list(item.practice, 'steps') + '</section>';
      }
      if (item.caution) {
        h += '<section class="sec"><div class="caution"><strong>' + esc(L.caution) + '</strong>' + item.caution + '</div></section>';
      }
      if (item.products && item.products.length) {
        h += '<section class="sec"><p class="section-label">' + esc(L.products) + '</p>' +
          item.products.map(function (p) {
            return '<div class="prole"><b>' + esc(p[0]) + '</b><span>' + p[1] + '</span></div>';
          }).join('') + '</section>';
      }
      if (item.quiz) {
        h += '<section class="sec"><p class="section-label">' + esc(L.quiz) + '</p>' +
          '<div class="quiz" data-quiz><p class="q">' + esc(item.quiz.q) + '</p><div class="opts">' +
          item.quiz.options.map(function (o, i) {
            return '<button class="opt" data-opt="' + i + '"><span class="mk">' +
              String.fromCharCode(65 + i) + '</span><span>' + esc(o) + '</span></button>';
          }).join('') +
          '</div><p class="why" data-why></p></div></section>';
      }
      return h;
    }

    /* ---------- chrome ---------- */
    function navHtml() {
      if (mode === 'full' && P.units) {
        var cur = items[active].unit || 0;
        return P.units.map(function (u, i) {
          var ids = items.map(function (x, k) { return (x.unit || 0) === i ? k : -1; }).filter(function (k) { return k > -1; });
          var d = ids.filter(function (k) { return done.has(k); }).length;
          return '<button class="unit-tab' + (i === cur ? ' on' : '') + '" data-unit="' + i + '">' +
            '<b>' + esc(u.title) + '</b><span>' + d + ' / ' + ids.length + '</span></button>';
        }).join('');
      }
      return items.map(function (s, i) {
        return '<button class="step-tab' + (i === active ? ' on' : '') + (done.has(i) ? ' done' : '') +
          '" data-step="' + i + '"><span class="n">' + pad(i + 1) + '</span><span class="t">' + esc(s.nav) + '</span></button>';
      }).join('');
    }

    function sideHtml() {
      if (mode === 'full' && P.units) {
        var cur = items[active].unit || 0;
        return '<div class="outline">' + P.units.map(function (u, i) {
          var ids = items.map(function (x, k) { return (x.unit || 0) === i ? k : -1; }).filter(function (k) { return k > -1; });
          return '<details class="ogroup"' + (i === cur ? ' open' : '') + '><summary><span>' + esc(u.title) +
            '</span><span>' + ids.filter(function (k) { return done.has(k); }).length + ' / ' + ids.length + '</span></summary>' +
            ids.map(function (k) {
              return '<button class="olink' + (k === active ? ' on' : '') + (done.has(k) ? ' done' : '') +
                '" data-step="' + k + '"><span class="qn">' + (k + 1) + '</span><strong>' + esc(items[k].title) +
                '</strong><span class="qc">&#10003;</span></button>';
            }).join('') + '</details>';
        }).join('') + '</div>';
      }
      return '<div class="qlist">' + items.map(function (s, i) {
        return '<button class="qitem' + (i === active ? ' on' : '') + (done.has(i) ? ' done' : '') +
          '" data-step="' + i + '"><span class="qn">' + (i + 1) + '</span><span><strong>' + esc(s.nav) +
          '</strong><small>' + esc(s.meta || '') + '</small></span><span class="qc">&#10003;</span></button>';
      }).join('') + '</div>';
    }

    /* ---------- full render ---------- */
    function paint() {
      var item = items[active];
      root.dataset.accent = item.accent || data.accent || 'blue';

      root.querySelector('[data-eyebrow]').textContent = P.eyebrow || '';
      root.querySelector('[data-title]').innerHTML = P.title || '';
      root.querySelector('[data-lede]').innerHTML = P.lede || '';
      root.querySelector('[data-facts-b]').textContent = P.factsB || '';
      root.querySelector('[data-facts-s]').textContent = P.factsS || '';
      root.querySelector('[data-side-label]').textContent = P.sideLabel || '';
      root.querySelector('[data-side-title]').textContent = P.sideTitle || L.outline;
      root.querySelector('[data-source]').innerHTML = P.sourceNote || '';
      root.querySelector('[data-footer]').innerHTML = P.footer || '';
      root.querySelector('[data-progress-label]').textContent = L.progress;
      root.querySelector('[data-progress-count]').textContent = done.size + ' / ' + items.length;
      root.querySelector('[data-bar]').style.width = (items.length ? (done.size / items.length * 100) : 0) + '%';

      root.querySelector('[data-nav]').innerHTML = navHtml();
      root.querySelector('[data-side]').innerHTML = sideHtml();

      var picker = root.querySelector('[data-picker]');
      picker.innerHTML = items.map(function (s, i) {
        return '<option value="' + i + '"' + (i === active ? ' selected' : '') + '>' + (i + 1) + '. ' + esc(s.title || s.nav) + '</option>';
      }).join('');

      var unitTitle = (mode === 'full' && P.units) ? esc(P.units[item.unit || 0].title) + ' &middot; ' : '';
      var numLabel = mode === 'full' ? L.lesson : L.step;

      /* On the last step a disabled next button is a dead end. Offer the way
         back to the module library instead. */
      var atEnd = active === items.length - 1;
      var libUrl = root.dataset.library || '';

      /* The three parts of a lesson are built as separate elements rather than
         concatenated into one string. Lesson bodies are written by hand, so a
         stray tag is possible; given its own element the parser repairs it at
         the end of the body instead of swallowing the buttons below. */
      var lesson = root.querySelector('[data-lesson]');
      lesson.textContent = '';

      lesson.appendChild(el('lesson-top',
        '<div><span class="kicker">' + unitTitle + numLabel + ' ' + pad(active + 1) + '</span>' +
        '<h2>' + esc(item.title) + '</h2><p class="one-line">' + (item.summary || '') + '</p></div>' +
        (item.tag ? '<span class="tag">' + esc(item.tag) + '</span>' : '')));

      var video = videoHtml(active);
      if (video) lesson.appendChild(el('lesson-video', video));

      lesson.appendChild(el('lesson-body', compose(item)));

      lesson.appendChild(el('actions',
        '<button class="navbtn" data-prev' + (active === 0 ? ' disabled' : '') + '>&#8592; ' + esc(L.previous) + '</button>' +
        '<button class="donebtn' + (done.has(active) ? ' done' : '') + '" data-done>' +
        esc(done.has(active) ? L.completed : L.complete) + '</button>' +
        (atEnd && libUrl
          ? '<a class="navbtn tolib" href="' + esc(libUrl) + '">' + esc(L.backToLibrary) + ' &#8594;</a>'
          : '<button class="navbtn" data-next' + (atEnd ? ' disabled' : '') + '>' + esc(L.next) + ' &#8594;</button>')));

      bindLesson(item);
      root.querySelectorAll(LANG_BTN).forEach(function (b) {
        b.classList.toggle('on', b.dataset.lang === lang);
      });
    }

    function go(i) {
      if (i < 0 || i >= items.length) return;
      active = i; saveProgress(); paint();
      if (window.innerWidth < 980) {
        root.querySelector('[data-lesson]').scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    function bindLesson(item) {
      root.querySelector('[data-prev]').addEventListener('click', function () { go(active - 1); });
      var nextBtn = root.querySelector('[data-next]');
      if (nextBtn) { nextBtn.addEventListener('click', function () { go(active + 1); }); }
      root.querySelector('[data-done]').addEventListener('click', function () {
        if (done.has(active)) { done.delete(active); } else { done.add(active); }
        saveProgress(); paint();
      });

      var quiz = root.querySelector('[data-quiz]');
      if (quiz && item.quiz) {
        var why = quiz.querySelector('[data-why]');
        quiz.querySelectorAll('[data-opt]').forEach(function (b) {
          b.addEventListener('click', function () {
            var picked = Number(b.dataset.opt);
            quiz.querySelectorAll('[data-opt]').forEach(function (x) {
              var i = Number(x.dataset.opt);
              x.classList.remove('right', 'wrong');
              if (i === item.quiz.answer) x.classList.add('right');
              else if (i === picked) x.classList.add('wrong');
            });
            why.innerHTML = '<strong>' + esc(picked === item.quiz.answer ? L.correct : L.incorrect) + '</strong> ' + (item.quiz.why || '');
            why.classList.add('show');
          });
        });
      }

      /* interactive selectors declared by a lesson body, e.g. the Action Gate */
      root.querySelectorAll('[data-select]').forEach(function (group) {
        var out = root.querySelector('[data-select-out="' + group.dataset.select + '"]');
        if (!out) return;
        var opts = item.selects && item.selects[group.dataset.select];
        if (!opts) return;
        var show = function (k) {
          var d = opts[k];
          if (!d) return;
          out.dataset.gate = k;
          out.innerHTML = '<strong>' + esc(d[0]) + '</strong><p>' + d[1] + '</p>';
        };
        var first = group.querySelector('.choice');
        group.querySelectorAll('.choice').forEach(function (b) {
          b.addEventListener('click', function (e) {
            /* These buttons sit inside the module root, which carries its own
               data-lang. Stop the click here so the delegated handler below
               never has to decide whether it was a language request. */
            e.stopPropagation();
            group.querySelectorAll('.choice').forEach(function (x) { x.classList.remove('on'); });
            b.classList.add('on');
            show(b.dataset.value);
          });
        });
        if (first) { first.classList.add('on'); show(first.dataset.value); }
      });
    }

    /* ---------- delegated chrome events ---------- */
    root.addEventListener('click', function (e) {
      var step = e.target.closest('[data-step]');
      if (step) { go(Number(step.dataset.step)); return; }
      var unit = e.target.closest('[data-unit]');
      if (unit) {
        var u = Number(unit.dataset.unit);
        for (var i = 0; i < items.length; i++) { if ((items[i].unit || 0) === u) { go(i); return; } }
        return;
      }
      var lb = e.target.closest(LANG_BTN);
      if (lb && lb.dataset.lang !== lang) {
        /* Keep the learner on the step they were reading. Progress is stored
           per language, so load() would otherwise drop them back to step one. */
        var keep = active;
        lang = lb.dataset.lang;
        put('ce-ac-lang', lang);
        root.setAttribute('lang', lang);
        load();
        if (keep >= 0 && keep < items.length) { active = keep; saveProgress(); }
        paint();
      }
    });

    root.querySelector('[data-picker]').addEventListener('change', function (e) {
      go(Number(e.target.value));
    });

    load();
    root.setAttribute('lang', lang);
    paint();
  };

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-ce-academy]').forEach(window.ceAcademy);
  });
})();
